import { Injectable } from '@angular/core';

/**
 * Service for encrypting and decrypting data stored in localStorage
 * Uses base64 encoding for obfuscation with XOR cipher
 * For production: Install and use crypto-js for stronger encryption (AES)
 * npm install crypto-js @types/crypto-js
 */
@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  // Secret key for XOR encryption/decryption
  // In production, this should be stored securely and fetched from a secure source
  private readonly SECRET_KEY = 'AiBillingPlatform2026SecretKey';

  constructor() {}

  /**
   * Encrypt data using XOR cipher and base64 encoding
   * @param data - Data to encrypt (string, object, or array)
   * @returns Encrypted string
   */
  encrypt(data: any): string {
    try {
      const stringData = typeof data === 'string' ? data : JSON.stringify(data);
      const encrypted = this.xorCipher(stringData, this.SECRET_KEY);
      return btoa(encrypted); // Base64 encode
    } catch (error) {
      console.error('Encryption error:', error);
      return '';
    }
  }

  /**
   * Decrypt data using base64 decoding and XOR cipher
   * @param encryptedData - Encrypted string
   * @returns Decrypted data
   */
  decrypt(encryptedData: string): any {
    try {
      const decoded = atob(encryptedData); // Base64 decode
      const decryptedString = this.xorCipher(decoded, this.SECRET_KEY);
      
      if (!decryptedString) {
        console.error('Decryption failed: empty result');
        return null;
      }

      // Try to parse as JSON, if fails return as string
      try {
        return JSON.parse(decryptedString);
      } catch {
        return decryptedString;
      }
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }

  /**
   * XOR cipher implementation
   * @param text - Text to encrypt/decrypt
   * @param key - Encryption key
   * @returns Encrypted/Decrypted text
   */
  private xorCipher(text: string, key: string): string {
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
      );
    }
    return result;
  }

  /**
   * Set encrypted item in localStorage
   * @param key - Storage key
   * @param data - Data to store (will be encrypted)
   */
  setItem(key: string, data: any): void {
    try {
      const encrypted = this.encrypt(data);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error setting encrypted item:', error);
    }
  }

  /**
   * Get and decrypt item from localStorage
   * @param key - Storage key
   * @returns Decrypted data or null if not found
   */
  getItem(key: string): any {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) {
        return null;
      }
      return this.decrypt(encrypted);
    } catch (error) {
      console.error('Error getting encrypted item:', error);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   * @param key - Storage key
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    localStorage.clear();
  }

  /**
   * Check if key exists in localStorage
   * @param key - Storage key
   * @returns true if key exists
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Set encrypted item in sessionStorage
   * @param key - Storage key
   * @param data - Data to store (will be encrypted)
   */
  setSessionItem(key: string, data: any): void {
    try {
      const encrypted = this.encrypt(data);
      sessionStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error setting encrypted session item:', error);
    }
  }

  /**
   * Get and decrypt item from sessionStorage
   * @param key - Storage key
   * @returns Decrypted data or null if not found
   */
  getSessionItem(key: string): any {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) {
        return null;
      }
      return this.decrypt(encrypted);
    } catch (error) {
      console.error('Error getting encrypted session item:', error);
      return null;
    }
  }

  /**
   * Remove item from sessionStorage
   * @param key - Storage key
   */
  removeSessionItem(key: string): void {
    sessionStorage.removeItem(key);
  }
}
